/**
 * uc-core expects the login password AES-256-CBC encrypted as "ivHex:cipherHex" with the key from
 * the public GET /app-config (src/modules/app-config/app-config.service.ts#decryptPassword).
 * WebCrypto's AES-CBC uses PKCS#7 padding, which matches Node's createCipheriv default.
 *
 * This is transport obfuscation the backend requires, not a secret: the key is public. TLS is
 * what protects the password.
 */
import { z } from 'zod'
import { api } from '@/api'

const appConfigSchema = z.looseObject({
  encryption: z.looseObject({
    key: z.string().min(1),
    algorithm: z.string().optional(),
    ivLength: z.number().int().positive().default(16),
  }),
})

let cached: Promise<{ key: CryptoKey; ivLength: number }> | null = null

async function loadKey(): Promise<{ key: CryptoKey; ivLength: number }> {
  const cfg = await api.get('/app-config', { schema: appConfigSchema, anonymous: true })
  if (cfg.encryption.algorithm && cfg.encryption.algorithm.toLowerCase() !== 'aes-256-cbc') {
    throw new Error(`Unsupported password cipher ${cfg.encryption.algorithm}`)
  }
  const raw = new TextEncoder().encode(cfg.encryption.key)
  const key = await crypto.subtle.importKey('raw', raw, { name: 'AES-CBC' }, false, ['encrypt'])
  return { key, ivLength: cfg.encryption.ivLength }
}

export function toHex(bytes: ArrayBuffer | Uint8Array): string {
  const u8 = bytes instanceof Uint8Array ? bytes : new Uint8Array(bytes)
  return Array.from(u8, (b) => b.toString(16).padStart(2, '0')).join('')
}

/** Encrypt with an already-imported key (exported for tests). */
export async function encryptWithKey(
  plain: string,
  key: CryptoKey,
  ivLength = 16,
): Promise<string> {
  const iv = crypto.getRandomValues(new Uint8Array(ivLength))
  const ct = await crypto.subtle.encrypt(
    { name: 'AES-CBC', iv },
    key,
    new TextEncoder().encode(plain),
  )
  return `${toHex(iv)}:${toHex(ct)}`
}

export async function encryptPassword(plain: string): Promise<string> {
  cached ??= loadKey().catch((e) => {
    cached = null
    throw e
  })
  const { key, ivLength } = await cached
  return encryptWithKey(plain, key, ivLength)
}
